import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup } from "@angular/forms";
import { Select, Store } from "@ngxs/store";
import { Observable, Subject } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";
import { PageEvent } from "@angular/material/paginator";
import { Sample, SampleType } from "@core/data";
import { Crop, Library } from '@library';
import { LIBRARY } from '../../library';
import { coerseDateProperty } from "@core/utils";
import { SampleIndexQuery } from "../sample-index/sample-index.state-model";
import {
    ClearSamplePickerFilter,
    InitSamplePicker,
    PageSamplePicker,
    QuerySamplePicker,
    SortSamplePicker,
} from "./image-move-sample-picker.state-actions";
import { ImageMoveSamplePickerState } from "./image-move-sample-picker.state";
import { ImageMoveSamplePickerStateModel } from "./image-move-sample-picker.state-model";

export interface ImageMoveSamplePickerDialogData {
    orgKey: string;
    sampleType: SampleType;
    excludeSampleKey: string;
}

@Component({
    selector: 'pv-image-move-sample-picker-dialog',
    templateUrl: 'image-move-sample-picker.dialog.html',
    styleUrls: ['image-move-sample-picker.dialog.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false,
    host: {
        class: 'pv-fullscreen-dialog'
    }
})
export class ImageMoveSamplePickerDialog implements OnInit, OnDestroy {

    @Select(ImageMoveSamplePickerState)
    state$: Observable<ImageMoveSamplePickerStateModel>;

    @Select(ImageMoveSamplePickerState.query)
    query$: Observable<SampleIndexQuery>;

    orderControl = new FormControl('desc');
    columnControl = new FormControl('updatedAt');
    sortFormGroup = new FormGroup({
        column: this.columnControl,
        order: this.orderControl,
    });

    searchControl = new FormControl();
    scionCultivarsControl = new FormControl([]);
    sitesControl = new FormControl([]);
    scionCropIdControl = new FormControl([]);
    startEvalPeriodControl = new FormControl(null);
    endEvalPeriodControl = new FormControl(null);

    queryFormGroup = new FormGroup({
        search: this.searchControl,
        scionCultivars: this.scionCultivarsControl,
        sites: this.sitesControl,
        scionCropId: this.scionCropIdControl,
        startEvalPeriod: this.startEvalPeriodControl,
        endEvalPeriod: this.endEvalPeriodControl,
    });

    cropOptions: Crop[];

    selectedSampleKey: string;
    private _selectedSample: Sample;

    private _destroy$ = new Subject();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: ImageMoveSamplePickerDialogData,
        private _store: Store,
        private _ref: MatDialogRef<ImageMoveSamplePickerDialog>,
        @Inject(LIBRARY) private _library: Library,
    ) { }

    ngOnInit(): void {

        this.cropOptions = this._library.filterExcludedCrops();

        this._store.dispatch(new InitSamplePicker(this.data.orgKey, this.data.sampleType, this.data.excludeSampleKey));

        // Keeps the visible form in sync with state - notably after resetFilter()
        // dispatches ClearSamplePickerFilter, so "Clear Filters" doesn't leave stale
        // values in the chips/search/date fields that a later keystroke would resend.
        this.query$.pipe(takeUntil(this._destroy$))
            .subscribe(query =>
                this.queryFormGroup.patchValue(query, { emitEvent: false })
            );

        this.queryFormGroup.valueChanges
            .pipe(debounceTime(300), takeUntil(this._destroy$))
            .subscribe(val => {
                let data: SampleIndexQuery = {
                    ...val,
                    startEvalPeriod: val.startEvalPeriod ? coerseDateProperty(val.startEvalPeriod) : null,
                    endEvalPeriod: val.endEvalPeriod ? coerseDateProperty(val.endEvalPeriod) : null,
                };

                this._store.dispatch(new QuerySamplePicker(data));
            });

        this.sortFormGroup.valueChanges
            .pipe(debounceTime(200), takeUntil(this._destroy$))
            .subscribe(val => {
                this._store.dispatch(new SortSamplePicker(val.column, val.order));
            });
    }

    toggleSortOrder(event: MouseEvent) {
        event.stopPropagation();
        this.orderControl.setValue(this.orderControl.value === 'asc' ? 'desc' : 'asc');
    }

    paginate(page: PageEvent) {
        this._store.dispatch(new PageSamplePicker(page.pageIndex, page.pageSize));
    }

    selectSample(sample: Sample) {
        if (this.selectedSampleKey === sample.key) {
            this.selectedSampleKey = null;
            this._selectedSample = null;
            return;
        }
        this.selectedSampleKey = sample.key;
        this._selectedSample = sample;
    }

    resetFilter() {
        this._store.dispatch(new ClearSamplePickerFilter());
    }

    select() {
        if (!this._selectedSample) return;
        this._ref.close(this._selectedSample);
    }

    cancel() {
        this._ref.close(null);
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }
}
